import { NodeId } from '@candulabs/craft';

import { getNodeId } from '@candu-ai/styleguide/selectors';
import { CssSelectorDefinitions, CssSelectorId } from 'craft/types';

export const getDocumentId = (): number | undefined => {
  // @ts-expect-error
  const documentId = document.querySelector('[data-document-id]')?.dataset?.documentId;

  return parseInt(documentId, 10);
};

export const getDocumentVersionId = (): string | undefined => {
  // @ts-expect-error
  return document.querySelector('[data-version-id]')?.dataset?.versionId;
};

export type OffsetsAndStyles = {
  left: number;
  top: number;
  documentOffsetTop: number;
  documentOffsetLeft: number;
  documentWidth: number;
  documentHeight: number;
  documentPadding: { top: number; right: number; bottom: number; left: number };
};

export const getOffsetAndStylesFromParent = (
  dom: HTMLElement | null,
  parent: HTMLElement | Element | null,
): OffsetsAndStyles => {
  if (!isDomElement(parent) || !isDomElement(dom) || !(parent instanceof HTMLElement)) {
    return {
      top: 0,
      left: 0,
      documentOffsetTop: 0,
      documentOffsetLeft: 0,
      documentWidth: 0,
      documentHeight: 0,
      documentPadding: { top: 0, right: 0, bottom: 0, left: 0 },
    };
  }

  const { scrollTop, scrollLeft, offsetTop, offsetLeft } = parent;

  const parentBounds = parent.getBoundingClientRect();
  const bounds = dom.getBoundingClientRect();
  const parentStyles = window.getComputedStyle(parent);

  const top = scrollTop + bounds.top - parentBounds.top;
  const left = scrollLeft + bounds.left - parentBounds.left;

  const documentWidth =
    parentBounds.width -
    parseFloat(parentStyles.paddingLeft) -
    parseFloat(parentStyles.paddingRight);
  const documentHeight = parentBounds.height;
  const documentPadding = {
    top: parseFloat(parentStyles.paddingTop),
    right: parseFloat(parentStyles.paddingRight),
    bottom: parseFloat(parentStyles.paddingBottom),
    left: parseFloat(parentStyles.paddingLeft),
  };

  return {
    top,
    left,
    documentOffsetTop: offsetTop,
    documentOffsetLeft: offsetLeft,
    documentWidth,
    documentHeight,
    documentPadding,
  };
};

export const getAllDomNodesFromDocument = (
  nodeId: NodeId,
  collectionItemId?: string,
  cssSelectorId?: CssSelectorId,
): HTMLElement[] => {
  if (collectionItemId) {
    return Array.from(document.querySelectorAll(`[data-nid^="${nodeId}-${collectionItemId}`));
  }

  if (cssSelectorId && cssSelectorId !== CssSelectorId.Root) {
    // @ts-ignore
    const selector = CssSelectorDefinitions[cssSelectorId].selector || '';

    return Array.from(document.querySelectorAll(`#${getNodeId(nodeId)} ${selector}`));
  }

  return Array.from(document.querySelectorAll(`[data-nid^="${nodeId}`));
};

export const getDomNodeFromDocument = (
  nodeId: NodeId,
  collectionItemId?: string,
  cssSelectorId?: CssSelectorId,
): HTMLElement | null => {
  if (collectionItemId) {
    return document.querySelector(
      `[data-nid^="${nodeId}-${collectionItemId}`,
    ) as HTMLElement | null;
  }
  if (cssSelectorId && cssSelectorId !== CssSelectorId.Root) {
    // @ts-ignore
    const selector = CssSelectorDefinitions[cssSelectorId].selector || '';
    return document.querySelector(`#${getNodeId(nodeId)} ${selector}`) as HTMLElement | null;
  }
  return document.querySelector(`[data-nid^="${nodeId}`) as HTMLElement | null;
};

export const isSourceElement = (dom: Element): boolean =>
  !!dom.getAttribute('data-collection-item-id') ||
  dom.getAttribute('data-cy') === 'node-collection';

export const isDomElement = (dom: any): dom is Element =>
  dom !== null && dom !== undefined && dom instanceof Element;
